import React, { useEffect } from 'react';
import { Container } from 'typescript-ioc';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import { connectZoomVM } from '../../zoom.context';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { Paging } from '../../../global/_paging/paging-container';
import { NoDataFound } from '../../../global/no-data';
import DefaultButton from '../../../global/default-button/default-button';
import { Link } from '../../../../infrastructure/components/router-extensions';
import { Routes } from '../../../../routes-table';
import { TimeWithTimezone } from '../../../global/time-in-different-time-zone';
import { SortOrder } from '../../../../api/shared.dto';
import { IZoomGroup } from '../../../../api/zoom.dao';
import { TScheduledZoomMeetingsListRequestSortKeys } from '../../../../api/zoom.dao';
import { MeetingsType } from '../../../../constants/MeetingsType';
import { ResourcesNavigation } from '../../../_events/_manage-event/shared/navigation/navigation';
import { IRouter } from '../../../../infrastructure/router';
import Text from '../../../global/text/text';
import { getNavigationLabels, SEPARATOR } from '../../../../constants/navigation-labels.constants';
import { ScheduledZoomMeetingsDateFilter } from './_scheduled-date-actions';
import { LOADING, TOPIC_TEXT } from '../../../../constants/common-labels.constants';

interface IZoomScheduledProps {
    pagingVM: PagingViewModel;
    scheduledMeetings: IScheduledMeetingRow[];
    scheduledMeetingsLength: number;
    pageNumber: number;
    pageSize: number;
    sortRows: (sortField: string, sortOrder: SortOrder) => void;
    sortOrder: SortOrder;
    sortField: string;
    fetchError: string;
    intl?: IntlShape;
}

interface IScheduledMeetingRow {
    id: number;
    startTime: string | null;
    startTimeInMeetingTimezone: string | null;
    endTime: string | null;
    endTimeInMeetingTimezone: string | null;
    timeZone: string;
    hostEmail: string;
    hostFirstName: string;
    hostLastName: string;
    topic: string;
    meetingId: string;
    type: string;
    hostGroups: IZoomGroup[];
}


const ScheduledMeetingRow = (props: IScheduledMeetingRow) => {
    const routingService = Container.get(IRouter);
    const routeParams = {meetingId: props.id.toString()};

    const handleDetailsClick = () => {
        routingService.navigate(props.type === MeetingsType.WEBINAR ? Routes.ZoomScheduledMeetingWebinarDetails :
        Routes.ZoomScheduledMeetingDetails, routeParams);
    };

    return (
        <tr
            className='scheduled-meeting-row'
            onDoubleClick={handleDetailsClick}
        >
            <td className='hoverable'>
                {props.startTime
                    ?
                     <>
                         {props.startTime}
                         {(props.startTime.valueOf() !== props.startTimeInMeetingTimezone.valueOf()) &&
                             <>
                                 <br/>
                                 <TimeWithTimezone time={props.startTimeInMeetingTimezone} timeZone={props.timeZone}/>
                             </>
                         }
                     </>
                     :
                     null
                }
            </td>
            <td className='hoverable'>
                {props.endTime
                    ?
                        <>
                            {props.endTime}
                            {(props.endTime.valueOf() !== props.endTimeInMeetingTimezone.valueOf()) &&
                                <>
                                    <br/>
                                    <TimeWithTimezone time={props.endTimeInMeetingTimezone} timeZone={props.timeZone}/>
                                </>
                            }
                        </>
                        :
                        null
                }
            </td>
            <Text className='hoverable' tag='td' message={props.hostEmail} withEllipsis />
            <Text className='hoverable' tag='td' message={props.hostFirstName} withEllipsis />
            <Text className='hoverable' tag='td' message={props.hostLastName} withEllipsis />
            <td className='hoverable'>{props.hostGroups ? props.hostGroups.map(g => g.name).join(', ') : ''}</td>
            <td className='hoverable'>{props.topic}</td>
            <Text className='hoverable' tag='td' message={props.meetingId} withEllipsis />
            <td className='hoverable align-right'>
                <Link
                    className='visible-while-hovered'
                    routeEnum={props.type === MeetingsType.WEBINAR ? Routes.ZoomScheduledMeetingWebinarDetails : Routes.ZoomScheduledMeetingDetails}
                    routeParams={routeParams}
                >
                    <DefaultButton
                        modifier='slim'
                        text={
                            <FormattedMessage
                                id='oe.common.details'
                                defaultMessage='Details'
                                description='Label text'
                            />
                        }/>
                </Link>
            </td>
        </tr>
    );
};

const ScheduledZoomMeetingsPresentation = (props: IZoomScheduledProps) => {
    const {scheduledMeetings, fetchError, intl} = props;
    const labels = getNavigationLabels(intl);
    const showMeetingsList = !!(!fetchError && scheduledMeetings && props.scheduledMeetingsLength);
    const showFetchingMeetings = !scheduledMeetings && !fetchError;
    const showNoMeetings = !fetchError && scheduledMeetings && !props.scheduledMeetingsLength;

    const theads: ITheadSort<TScheduledZoomMeetingsListRequestSortKeys>[] = [
        {
            title: intl.formatMessage({
                id: `oe.datetime.start`,
                defaultMessage: 'Start Datetime',
                description: 'Label text',
            }), sortField: 'startTime', width: '20%'
        },
        {
            title: intl.formatMessage({
                id: `oe.datetime.end`,
                defaultMessage: 'End Datetime',
                description: 'Label text',
            }), sortField: 'endTime', width: '20%'
        },
        {
            title: intl.formatMessage({
                id: `oe.host.email`,
                defaultMessage: 'Host Email',
                description: 'Label text',
            }), sortField: 'zoomUser.email', width: '20%'
        },
        {
            title: intl.formatMessage({
                id: `oe.host.firstName`,
                defaultMessage: 'Host First Name',
                description: 'Label text',
            }), sortField: 'zoomUser.firstName', width: '20%'
        },
        {
            title: intl.formatMessage({
                id: `oe.host.lastName`,
                defaultMessage: 'Host Last Name',
                description: 'Label text',
            }), sortField: 'zoomUser.lastName', width: '20%'
        },
        {
            title: intl.formatMessage({
                id: `oe.host.groups`,
                defaultMessage: 'Host Groups',
                description: 'Label text',
            }), width: '15%'
        },
        {
            title: intl.formatMessage(TOPIC_TEXT), sortField: 'topic', width: '35%'
        },
        {
            title: intl.formatMessage({
                id: `oe.meeting.meetingId`,
                defaultMessage: 'Meeting ID',
                description: 'Label text',
            }), width: '15%'
        },
        { width: '100px' }
    ];

    const sortOptions = {
        sortRows: props.sortRows,
        sortField: props.sortField,
        sortOrder: props.sortOrder,
        theads: theads
    };

    useEffect(() => {
        document.title = `${labels.ROOT} ${SEPARATOR} ${labels.RESOURCES_LIST} ${SEPARATOR} ${labels.ZOOM_NAVIGATION.MEETINGS}`;
    }, []);

    return (
        <div className='scheduled-meetings-list'>
            <section className='resources-tabs horizontal-row scheduled-meetings-list__horizontal-row'>
                <ResourcesNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <Link routeEnum={Routes.ZoomScheduleNewMeeting}>
                        <DefaultButton modifier={['fat', 'confirm-safe']} text={
                            <FormattedMessage
                                id='oe.zoom.scheduleNewMeeting'
                                defaultMessage='Schedule New Zoom Meeting'
                                description='Label text'
                            />
                        }/>
                    </Link>
                </div>
            </section>
            <section className='scheduled-meetings-list__filters'>
                <ScheduledZoomMeetingsDateFilter/>
            </section>
            <section className='zoom-meetings-list fixed-list-layout'>
                <ListLayout sortOptions={sortOptions}>
                    {showMeetingsList && scheduledMeetings.map(x => <ScheduledMeetingRow {...x} key={x.id}/>)}
                    {showFetchingMeetings && <tr>
                        <td className='table-loading' colSpan={12}>
                            <FormattedMessage
                                {...LOADING}
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </td>
                    </tr>}
                    {fetchError && <tr>
                        <td className='table-loading' colSpan={12}>{fetchError}</td>
                    </tr>}
                    {showNoMeetings && <tr>
                        <td className='table-loading' colSpan={12}>
                            <NoDataFound imageDescriptionText={
                                <FormattedMessage
                                    id='oe.meetings.noMeetingsFound'
                                    defaultMessage='No meetings found'
                                    description='Label text'
                                />
                            }/>
                        </td>
                    </tr>}
                </ListLayout>
            </section>
            <Paging pagingVM={props.pagingVM}/>
        </div>
    );
};

const ScheduledZoomMeetingsPage = injectIntl(connectZoomVM<IZoomScheduledProps>(ScheduledZoomMeetingsPresentation, zoomVM => {
    const scheduledMeetings = zoomVM.getScheduledMeetingsVM().getScheduledMeetings();
    const pagingVM = zoomVM.getScheduledMeetingsVM().getPagingVM();
    const scheduledMeetingsVM = zoomVM.getScheduledMeetingsVM();

    return {
        pagingVM: pagingVM,
        scheduledMeetings: scheduledMeetings,
        scheduledMeetingsLength: scheduledMeetings && scheduledMeetings.length,
        pageNumber: pagingVM.getCurrentPage(),
        pageSize: pagingVM.getPageSize(),
        sortRows: scheduledMeetingsVM.sortTableRows.bind(scheduledMeetingsVM),
        sortField: scheduledMeetingsVM.getSortField(),
        sortOrder: scheduledMeetingsVM.getSortOrder(),
        fetchError: scheduledMeetingsVM.getFetchError(),
    };
}));


export { ScheduledZoomMeetingsPage, IScheduledMeetingRow, ScheduledMeetingRow, ScheduledZoomMeetingsPresentation };